import _ from 'lodash';
import React, { Component } from 'react';
import { Col, FormLabel, FormControl, FormGroup, FormText, FormCheck, Row } from 'react-bootstrap';
import wordwrap from 'word-wrap';
import Pikaday from '../../report/vendor/pikaday/pikaday-dsi';
import Api from '../../../services/ApiService';
import ROInput from './ROInput';

/* These handlers and validators are shared against all modes (DRY).

   This is conceptually an abstract class, thus this component should not be used
   directly.  The following properties and methods should be considered abstract
   and require implementation in inherited classes:

   defaultFocus, schema, state, process(), render()
*/

export default class ModeComponent extends Component {
  componentDidMount() {
    /* the setTimeout is necessary to prevent a double-TAB like UX occurring if
       a user TABs out of the mode selector.  If they TAB out of it without a setTimeout
       here, essentially what happens is the focus occurs, but then the tab key event
       default DOM action is processed, advancing the focus twice, which we don't want.
    */
    setTimeout(() => {
      // react render order can be weird at times - focusEl almost always exists, but
      // occassionally it doesn't, check to prevent exceptions
      const focusEl = document.getElementById(this.defaultFocus);
      if (focusEl) {
        focusEl.focus();
      }
    }, 0);

    // bind pikaday where specified (employees/VacationTime.js, for example)
    this.pikadayObjs = [];
    if (this.schema) {
      for (const param of Object.keys(this.schema)) {
        if (this.schema[param].pikaday) {
          const pikadayObj = new Pikaday({
            blurFieldOnSelect: false,
            format: 'MM-DD-YY',
            field: document.getElementById(param),
            onSelect: () => {
              this.setState({
                [param]: {
                  value: pikadayObj.toString(),
                  validationState: null, // clear errors on change
                },
              });
            },
          });
          this.pikadayObjs.push(pikadayObj);
        }
      }
    }
  }

  componentWillUnmount() {
    this.pikadayObjs.forEach(pikadayObj => {
      pikadayObj.destroy();
    });
  }

  checkbox(id) {
    return (
      <FormGroup>
        <Col sm={{ span: 9, offset: 3 }}>
          <FormCheck inline>
            <FormCheck.Input
              type="checkbox"
              id={id}
              defaultChecked={this.state[id].value}
              value={this.state[id].value}
              onChange={this.handleChange}
            />
            <FormCheck.Label htmlFor={id}>{this.schema[id].label}</FormCheck.Label>
          </FormCheck>
        </Col>
      </FormGroup>
    );
  }

  handleBlur = e => {
    const id = e.target.id;
    const value = e.target.value;
    const validationState = this.validateField(id) ? null : 'error';

    if (validationState !== this.state[id].validationState) {
      this.setState({
        [id]: {
          value,
          validationState,
        },
      });
    }
  };

  handleChange = e => {
    const id = e.target.id;
    let value = null;

    switch (e.target.type) {
      case 'checkbox':
        value = e.target.checked;
        break;
      case 'textarea':
      case 'text':
      default:
        value = e.target.value;
        break;
    }

    this.setState({
      [id]: {
        value,
        validationState: null, // clear errors on change
      },
    });
  };

  // called from ModeConductor - override per mode if necessary
  process(userID, password) {
    if (this.validateForm()) {
      // run D3/QM command here (see timeclock.controller.js:processAction())
      const params = {};
      for (const param of Object.keys(this.schema)) {
        if (this.schema[param].multiline) {
          /* inject D3/QM newline markers (^^) where appropriate (70 chars/line max)
          1. normalize all browser textarea newline schemes to \n
          2. split lines longer than 70chars on ^^ delimeter
          3. join all lines (possibly split-edited) back on ^^
          */

          let value = this.state[param].value.trim();

          // 1. Normalize user supplied newlines generated when user presses the enter key
          //   (diff browsers use different schemes - do the regex IN ORDER)
          value = value.replace(/\r\n/g, '\n');
          value = value.replace(/\r/g, '\n');

          // 2. Textareas wrap on screen, but do NOT interject newlines into the actual value,
          //    so we'll do it ourselves with a wordwrap aglorithm that doesn't break words.
          const wordwrapOpts = {
            indent: '',
            newline: '^^',
            trim: true,
            width: 70,
          };
          value = value.split('\n'); // splits on any manually entered lines
          for (let i = 0; i < value.length; i++) {
            if (value[i].length > 70) {
              value[i] = wordwrap(value[i], wordwrapOpts);
            }
          }

          // 3. Join it all back together
          value = value.join('^^');

          params[param] = value;

          // else not multiline
        } else {
          params[param] = this.state[param].value;
        }
      }

      const postBody = {
        clockType: this.clockType,
        mode: this.mode,
        params,
        userID,
        password,
      };

      return new Promise((resolve, reject) => {
        Api.post('timeclock/processaction', postBody)
          .then(res => {
            res = res.data;
            if (res.d3status === 'OK') {
              resolve(res.d3message); // optional additional success info as a param
            } else {
              throw new Error(res.d3message);
            }
          })
          .catch(error => {
            reject(error.message);
          });
      });
    }

    // prevents process / reset if form is invalid (add string to pop toaster)
    return Promise.reject('');
  }

  validateField(id) {
    let isValidField = true;

    if (this.schema[id].required) {
      if (this.state[id].value.trim() === '') {
        isValidField = false;
      }
    }

    if ('regex' in this.schema[id]) {
      isValidField = this.schema[id].regex.test(this.state[id].value);
    }

    return isValidField;
  }

  validateForm() {
    let isValidForm = true;

    for (const id of Object.keys(this.schema)) {
      if (!this.validateField(id)) {
        this.setState(prevState => {
          return {
            [id]: {
              value: prevState[id].value,
              validationState: 'error',
            },
          };
        });
        isValidForm = false;
      }
    }

    return isValidForm;
  }

  validatedOperationInput(id, opModes) {
    return (
      <FormGroup as={Row} className="text-right">
        <Col as={FormLabel} sm={3}>
          {this.schema[id].label}
        </Col>
        <Col sm={9}>
          <FormControl
            as="select"
            id={id}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            value={this.state[id].value}
          >
            <option value="CK1979"> </option>
            {opModes &&
              opModes.map(opMode => (
                <option key={opMode.op} value={opMode.op} title={opMode.desc}>
                  {_.truncate(`${opMode.op} - ${opMode.desc}`, { length: 70, omission: ' ...' })}
                </option>
              ))}
          </FormControl>
          <FormControl.Feedback />
          <FormText style={{ visibility: this.state[id].validationState === 'error' ? 'visible' : 'hidden' }}>
            {this.schema[id].errorText}
          </FormText>
        </Col>
      </FormGroup>
    );
  }

  validatedROInput(id, updateOpModes, updateMileage) {
    const onChange = value => {
      this.setState({
        [id]: {
          value,
          validationState: null, // red "NOT FOUND" indicates error
        },
      });
    };

    // this comes from Process, which validates the whole form
    const showErrorText = this.state[id].validationState === 'error';

    return (
      <FormGroup as={Row} className="text-right">
        <Col as={FormLabel} sm={3}>
          {this.schema[id].label}
        </Col>
        <Col sm={9}>
          <ROInput
            id={id}
            onChange={onChange}
            errorText={this.schema[id].errorText}
            showErrorText={showErrorText}
            updateOpModes={updateOpModes}
            updateMileage={updateMileage}
          />
        </Col>
      </FormGroup>
    );
  }

  validatedTextInput(id) {
    // rows will be ignored if type input
    let value = this.state[id].value;
    let type = 'input';

    if (this.schema[id].multiline) {
      type = 'textarea';
      value = value.replace(/\\n/g, '\n');
    }

    return (
      <FormGroup as={Row} className="text-right">
        <Col as={FormLabel} sm={3}>
          {this.schema[id].label}
        </Col>
        <Col sm={9}>
          <FormControl
            as={type}
            id={id}
            className="p-0"
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            value={value}
            rows="4"
          />
          <FormControl.Feedback />
          <FormText style={{ visibility: this.state[id].validationState === 'error' ? 'visible' : 'hidden' }}>
            {this.schema[id].errorText}
          </FormText>
        </Col>
      </FormGroup>
    );
  }
}
